import type { SwadeContainerConfigurationInterface } from "@/types/items";
import type { ContainerArrayName } from '@/appdata/container-targets';
import { blankNormalise, composeDisplayName } from '@/appdata/container-display-name';

/**
 * How a container-namespace field folds the modifications targeting it. `textList` is text-list.ts's
 * fold (ruling 2026-09-24): `alter` overwrites, `add` joins with ", ", `subtract` removes an exact item.
 */
export type ContainerFieldFold = 'lastWins' | 'additiveSum' | 'textList';

/**
 * When a field is written. `override` fields are recomputed and reversed; `construction` fields are
 * written once when the container is created and are never touched again.
 */
export type ContainerFieldTiming = 'override' | 'construction';

/**
 * What an author may do to a field, declared beside the rule that resolves it (spec §7.1). The
 * authoring UI derives every operator list and value control from these — it lists nothing itself —
 * so a field gains authoring support by declaring it here or on its strategy, in one place.
 */
export type AuthoringOperator = 'add' | 'subtract' | 'multiply' | 'alter';
export type AuthoringValueKind = 'integer' | 'decimal' | 'text' | 'enum';
export interface AuthoringDeclaration {
    operators: readonly AuthoringOperator[];
    valueKind: AuthoringValueKind;
    /** enum only */
    values?: readonly string[];
    /** die only */
    valueTypes?: readonly ('step' | 'modify')[];
}

export interface ContainerFieldPolicy {
    fold: ContainerFieldFold;
    timing: ContainerFieldTiming;
}

/**
 * ONLY the special policies live here. This registry deliberately does NOT reproduce layer 3a's
 * supported-detail list — `resolveDetail` remains the sole authority for item detail fields, and a
 * `detail` this registry does not claim is delegated to it. Duplicating that list would give one
 * rule two homes, which is how layer 3a's parser divergence started.
 *
 * The two members are NOT read by the same code, and they do NOT have the same reach. Read this
 * before changing an entry below, because reclassifying a field is TWO edits, not one:
 *
 * - `timing` has THREE readers, and one declaration flips all of them.
 *   `registerContainerEffect` refuses to record a `construction` field in the effects ledger;
 *   `rebuildContainerOverrides` skips one while recomputing (belt-and-braces — the first guard
 *   means none should be in the ledger to find); and `seedContainerCharges` reads it to confirm
 *   the field is still construction-time before doing its single write. `charges` is the only
 *   `construction` entry, and that is precisely why a player can spend it without the ledger
 *   rebuilding it underneath them.
 * - `fold` has exactly ONE reader: `rebuildContainerOverrides`, which by the guard above only ever
 *   sees `override` fields and implements `lastWins` and `textList`. `seedContainerCharges` does NOT read it — it folds additively,
 *   unconditionally. So the `additiveSum` on `charges` below DESCRIBES what the seeder happens to
 *   do; it does not drive it, and giving a `construction` field `fold: 'lastWins'` would be
 *   silently ignored. Adding a `lastWins` branch to the seeder for a field that does not yet exist
 *   was deliberately declined: write that branch in the same change that adds the field.
 *
 * So: moving `charges` to `override` means changing the entry here AND teaching
 * `rebuildContainerOverrides` to fold `additiveSum` — it implements `lastWins` and `textList` and
 * `continue`s on anything else, so the entry alone would leave the field recorded in the ledger but never
 * rebuilt from it. That is the branch the bullet above says was deliberately declined; this is when
 * to write it. The three `seedContainerCharges` call sites do NOT double-write in the meantime:
 * the seeder's first statement returns unless the registry still says `construction`, so reclassifying
 * makes all three inert at once (`container-modification-charges-policy.test.ts` pins exactly that,
 * on a mocked `{ fold: 'additiveSum', timing: 'override' }`). Delete them anyway — dead call sites
 * on a creation path are worth removing — but as hygiene, not to avoid a double write.
 *
 * Adding a new `construction` field means the entry here AND a writer on the creation path that
 * honours the fold you gave it — this registry will not enforce that for you.
 */
const POLICIES: Record<string, ContainerFieldPolicy & AuthoringDeclaration> = {
    name:     { fold: 'lastWins',    timing: 'override',     operators: ['alter'], valueKind: 'text' },
    type:     { fold: 'lastWins',    timing: 'override',     operators: ['alter'], valueKind: 'enum', values: ['major', 'minor'] },
    // A text list (ruling 2026-09-24): add joins onto the player's trapping, subtract removes an exact item.
    trapping: { fold: 'textList',    timing: 'override',     operators: ['add', 'subtract', 'alter'], valueKind: 'text' },
    // add-only: seedContainerCharges ignores alter and multiply (a fresh container has no base).
    charges:  { fold: 'additiveSum', timing: 'construction', operators: ['add'],   valueKind: 'integer' },
};

export function containerFieldPolicy(detail: string): ContainerFieldPolicy | null {
    // hasOwnProperty rather than a bare lookup: `detail` is author-supplied data, and indexing an
    // object literal with 'constructor' or '__proto__' would return an inherited member.
    if (!Object.prototype.hasOwnProperty.call(POLICIES, detail)) return null;
    // Only the policy contract. The authoring declaration beside it is read through
    // policyDeclarationFor; returning it here would change what three existing readers receive.
    const { fold, timing } = POLICIES[detail];
    return { fold, timing };
}

/** Every field this registry claims, for the authoring layer to derive its lists from. */
export function policyDetails(): string[] {
    return Object.keys(POLICIES);
}

/** The authoring declaration of a policy field, or null. Own-property safe like containerFieldPolicy. */
export function policyDeclarationFor(detail: string): AuthoringDeclaration | null {
    if (!Object.prototype.hasOwnProperty.call(POLICIES, detail)) return null;
    const { operators, valueKind, values, valueTypes } = POLICIES[detail];
    return { operators, valueKind, ...(values ? { values } : {}), ...(valueTypes ? { valueTypes } : {}) };
}

/**
 * The policy fields a container kind holds in a REAL property of its own: field → property. The
 * rebuild writes these into the container itself, so an edit input bound to the property shows the
 * value (ruling 2026-09-15); a power's property is `trappings`, not `trapping`. Every other policy
 * field is SYNTHETIC — EdgeContainer has no name or trapping, ItemContainer no trapping, SkillRank no
 * name, Drawback no name or trapping — and lives in `configuration.container`. A drawback's `type`
 * is a real column, but stays synthetic by the same ruling.
 */
const OWN_FIELDS: Record<ContainerArrayName, Readonly<Record<string, string>>> = {
    equipment:  { name: 'name' },
    skills:     { trapping: 'trapping' },
    edges:      {},
    hindrances: {},
    powers:     { name: 'name', trapping: 'trappings' },
};

export function ownFieldsFor(arrayName: ContainerArrayName): Readonly<Record<string, string>> {
    return OWN_FIELDS[arrayName];
}

type MaybeContainer = {
    configuration?: SwadeContainerConfigurationInterface | Record<string, unknown> | null,
} & Record<string, unknown>;

/**
 * A container's field with its `configuration.container` override applied.
 *
 * The override wins; the base is whatever the field already held, which for a synthetic key is
 * nothing. A `null` override means "no override" and falls through to the base — the same rule
 * `resolveContainerDetails` applies, and for the same reason: a client-side clear produces
 * `{ trapping: null }` before any save prunes it.
 */
export function resolveContainerField(container: unknown, field: string): unknown {
    const source = (container ?? null) as MaybeContainer | null;

    // `field` can be author-supplied (a modification's `detail`), so both lookups must be own-key
    // only. A bare access returns an inherited member — 'toString' on any plain object yields
    // Object.prototype.toString — and a function leaking into a resolved sheet value renders as
    // source text or blows up a numeric fold. containerFieldPolicy guards its lookup the same way.
    const own = (target: unknown, key: string): unknown =>
        target !== null && target !== undefined && Object.prototype.hasOwnProperty.call(target, key)
            ? (target as Record<string, unknown>)[key]
            : null;

    const base = own(source, field);

    const overrides = (source?.configuration as SwadeContainerConfigurationInterface | null)?.container ?? null;

    // Mirror of resolveContainerDetails' guard. `configuration` is decoded JSON, so a malformed
    // payload can carry a string or a list here; indexing either would produce nonsense.
    if (overrides === null || typeof overrides !== 'object' || Array.isArray(overrides)) {
        return base;
    }

    return own(overrides, field) ?? base;
}

/**
 * What a container is called: the composed presentation label.
 *
 * Handles all four container shapes — items and edges name their catalog element `selection`,
 * skill ranks use `skill`, drawbacks use `hindrance`. Severity is appended only for a drawback
 * whose CATALOG hindrance type is 'choice' (the player picked major or minor); the value shown is
 * the drawback's own resolved type. Two different fields named `type`, one line apart.
 *
 * `fallbackTrapping` fills the trapping slot only when the container resolves none of its own — an
 * explicit trapping, or a container.* override of it, always wins. chosen-labels.ts passes the
 * labels of the container's applied choices here, so "Trademark Weapon" reads as
 * "Trademark Weapon (Light Mace)" until an author sets a trapping. Display only.
 */
export function containerDisplayName(container: unknown, fallbackTrapping: string | null = null): string {
    const source = (container ?? null) as (MaybeContainer & {
        selection?: { name?: string | null },
        skill?: { name?: string | null },
        hindrance?: { name?: string | null, type?: string | null },
    }) | null;

    const catalogName = source?.selection?.name
        ?? source?.skill?.name
        ?? source?.hindrance?.name
        ?? null;

    const severity = source?.hindrance?.type === 'choice'
        ? resolveDrawbackType(source)
        : null;

    return composeDisplayName(
        coerceContainerValue(resolveContainerField(source, 'name')),
        catalogName,
        severity,
        blankNormalise(resolveSkillTrapping(source)) ?? fallbackTrapping,
    );
}

/**
 * A resolved value narrowed to something a label can hold.
 *
 * The persist filter deliberately preserves false, 0, '' and arrays, so a resolved value is not
 * guaranteed to be a string. Booleans, arrays and objects are treated as ABSENT rather than
 * stringified — String(true) is 'true' and String({}) is '[object Object]', and neither is a label.
 * Mirrors coerceContainerValue() in the PHP repo's HasContainerConfiguration trait.
 *
 * One divergence from that PHP mirror: the `Number.isFinite` guard below treats NaN and Infinity as
 * absent (null), whereas PHP's `(string)` cast turns its equivalents into the strings "NAN" and
 * "INF". Unreachable in practice on either side of the wire — `configuration` is a decoded JSON
 * column, and both `JSON.parse` and PHP's `json_decode` reject `NaN`/`Infinity` literals with a
 * syntax error, so a non-finite number can never actually reach this function from real data.
 */
export function coerceContainerValue(value: unknown): string | null {
    if (typeof value === 'string') return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);

    return null;
}

/** A skill rank's trapping with its override applied. Null when the skill is untrapped. */
export function resolveSkillTrapping(rank: unknown): string | null {
    return coerceContainerValue(resolveContainerField(rank, 'trapping'));
}

/** A drawback's chosen severity with its override applied. */
export function resolveDrawbackType(drawback: unknown): string | null {
    return coerceContainerValue(resolveContainerField(drawback, 'type'));
}
